import React from 'react';
import styled from 'styled-components';

const ChartContainer = styled.div`
  width: 100%;
  height: 400px;
  background-color: #1A1A1A;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    height: 300px;
    padding: 15px;
  }
`;

const RadarGrid = styled.div`
  position: relative;
  width: 300px;
  height: 300px;
  border: 2px solid #333;
  border-radius: 50%;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 200px;
    height: 200px;
    border: 1px solid #333;
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100px;
    height: 100px;
    border: 1px solid #333;
    border-radius: 50%;
  }
  
  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
    
    &::before {
      width: 166px;
      height: 166px;
    }
    
    &::after {
      width: 83px;
      height: 83px;
    }
  }
`;

const GridLine = styled.div`
  position: absolute;
  width: 1px;
  height: 100%;
  background-color: #333;
  left: 50%;
  transform: translateX(-50%);
  
  &:nth-child(2) {
    transform: translateX(-50%) rotate(72deg);
  }
  
  &:nth-child(3) {
    transform: translateX(-50%) rotate(144deg);
  }
  
  &:nth-child(4) {
    transform: translateX(-50%) rotate(216deg);
  }
  
  &:nth-child(5) {
    transform: translateX(-50%) rotate(288deg);
  }
`;

const ScorePoint = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  background-color: #2196F3;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
`;

const ScoreLabel = styled.div`
  position: absolute;
  color: #FFFFFF;
  font-size: 12px;
  font-weight: 600;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 4;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const Legend = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  width: 100%;
  max-width: 500px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #FFFFFF;
  
  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background-color: #2196F3;
    border-radius: 50%;
    margin-right: 8px;
  }
`;

const SimpleRadarChart = ({ scores }) => {
  if (!scores) return null;

  const categories = [
    { name: 'Reliability', value: scores.reliability || 0, angle: 0 },
    { name: 'Performance', value: scores.performance || 0, angle: 72 },
    { name: 'Fuel Economy', value: scores.fuelEconomy || 0, angle: 144 },
    { name: 'Cost of Ownership', value: scores.totalCostOfOwnership || 0, angle: 216 },
    { name: 'Resale Value', value: scores.resaleValue || 0, angle: 288 }
  ];

  const getPointPosition = (value, angle, containerSize = 300) => {
    const maxRadius = (containerSize / 2) - 20;
    const radius = (value / 10) * maxRadius;
    const radian = (angle - 90) * (Math.PI / 180);
    
    const x = containerSize / 2 + radius * Math.cos(radian);
    const y = containerSize / 2 + radius * Math.sin(radian);
    
    return { x, y };
  };

  const getLabelPosition = (angle, containerSize = 300) => {
    const labelRadius = (containerSize / 2) + 30;
    const radian = (angle - 90) * (Math.PI / 180);
    
    const x = containerSize / 2 + labelRadius * Math.cos(radian);
    const y = containerSize / 2 + labelRadius * Math.sin(radian);
    
    return { x, y };
  };

  return (
    <ChartContainer>
      <RadarGrid>
        {[1, 2, 3, 4, 5].map(i => (
          <GridLine key={i} />
        ))}
        
        {categories.map((category, index) => {
          const pointPos = getPointPosition(category.value, category.angle);
          const labelPos = getLabelPosition(category.angle);
          
          return (
            <React.Fragment key={index}>
              <ScorePoint
                style={{
                  left: `${pointPos.x}px`,
                  top: `${pointPos.y}px`
                }}
              />
              <ScoreLabel
                style={{
                  left: `${labelPos.x}px`,
                  top: `${labelPos.y}px`
                }}
              >
                {category.name}
                <br />
                <span style={{ color: '#2196F3', fontSize: '10px' }}>
                  {category.value.toFixed(1)}
                </span>
              </ScoreLabel>
            </React.Fragment>
          );
        })}
      </RadarGrid>
      
      <Legend>
        {categories.map((category, index) => (
          <LegendItem key={index}>
            {category.name}: {category.value.toFixed(1)}/10
          </LegendItem>
        ))}
      </Legend>
    </ChartContainer>
  );
};

export default SimpleRadarChart;